// Gift review summary card. Mirrors data-component="gift-review".
// Shows the selected gift, size (shirts only), delivery destination,
// and the customer-agreement checkbox. The submit button changes label
// based on delivery mode ("Add to next shipment" vs "Ship now").
//
// TODO(runtime): submit button dispatches to
// POST /v1/actions/ACCOUNT_WORKFLOW_REQUEST_GIFT (proposed - TODO
// runtime; the function dist/account-workflows.requestGift is confirmed
// but no HTTP handler yet). Required payload: workId, commandId,
// subscriptionId, catalogId, variant, confirmed=true.
//
// W32 (2026-10-20): the Customer fact comes from the `customerName`
// prop (SARAH in preview, the real HubSpot profile on a real session)
// instead of the fixture import.
import type { ReactNode } from 'react';
import { SHIRT_SIZES, type GiftVariant } from '../mockData.js';

export interface GiftReviewProps {
  selected: GiftVariant | null;
  customerName: string;
  size: string;
  shipmentSummary: string;
  variantSummary: string;
  shippingSummary: string;
  agreementCopy: string;
  agreed: boolean;
  attachLabel: string;
  reviewNote: string;
  resultMessage: string | null;
  isShirt: boolean;
  canSubmit: boolean;
  onSizeChange: (size: string) => void;
  onAgreementChange: (agreed: boolean) => void;
  onAttach: () => void;
}

export function GiftReview({
  selected,
  customerName,
  size,
  shipmentSummary,
  variantSummary,
  shippingSummary,
  agreementCopy,
  agreed,
  attachLabel,
  reviewNote,
  resultMessage,
  isShirt,
  canSubmit,
  onSizeChange,
  onAgreementChange,
  onAttach,
}: GiftReviewProps): ReactNode {
  if (!selected) return null;
  return (
    <section className="card" data-component="gift-review">
      <div className="card-head">
        <h3>Review this gift</h3>
        <span className="small">Not submitted</span>
      </div>
      <div className="gift-review">
        <span className="gift-kind">SELECTED GIFT</span>
        <h2>{selected.name}</h2>
        <p className="selected-variant-label">{selected.variant} · Example</p>
        {isShirt ? (
          <div>
            <label htmlFor="shirt-size">Shirt size</label>
            <div className="sizes" id="shirt-size">
              {SHIRT_SIZES.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={s === size ? 'selected-size' : undefined}
                  onClick={() => onSizeChange(s)}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : null}
        <div className="gift-facts">
          <div>
            <span>Customer</span>
            <strong>{customerName}</strong>
          </div>
          <div>
            <span>Delivery</span>
            <strong>{shipmentSummary}</strong>
          </div>
          <div>
            <span>Variant</span>
            <strong>{variantSummary}</strong>
          </div>
          <div>
            <span>Gift price</span>
            <strong className="free-gift">$0.00</strong>
          </div>
          <div>
            <span>Shipping</span>
            <strong>{shippingSummary}</strong>
          </div>
          <div>
            <span>Repeats</span>
            <strong>No · One-time gift</strong>
          </div>
        </div>
        <label className="gift-agreement">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => onAgreementChange(e.target.checked)}
          />{' '}
          <span>{agreementCopy}</span>
        </label>
        <button
          className="blue-action"
          type="button"
          disabled={!canSubmit}
          onClick={onAttach}
        >
          {attachLabel}
        </button>
        <p className="gift-review-note">{reviewNote}</p>
        {resultMessage ? (
          <div role="status" className="gift-demo-result">
            {resultMessage}
          </div>
        ) : null}
      </div>
    </section>
  );
}
